import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, categoryColors } from '@/mocks/categories';
import { allArticles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'football');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeCategory]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const categoryArticles = allArticles.filter((a) => a.category === activeCategory);
  const colors = categoryColors[currentCategory?.color || 'primary'];

  return (
    <>
      <Navbar />
      <main>
        <section className="relative w-full h-[200px] md:h-[280px] flex items-end overflow-hidden">
          <div className="absolute inset-0 bg-foreground-900">
            <div className={`absolute inset-0 opacity-20 ${currentCategory?.color === 'primary' ? 'bg-primary-500' : currentCategory?.color === 'accent' ? 'bg-accent-500' : 'bg-secondary-500'}`}></div>
            <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-background-50 to-transparent"></div>
          </div>
          <div className="relative z-10 w-full pb-6 md:pb-8">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <Breadcrumb
                items={[
                  { label: '赛事分类', path: '/category/football' },
                  { label: currentCategory?.name || '' },
                ]}
              />
            </div>
          </div>
        </section>

        <section className="w-full py-8 md:py-12 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-wrap gap-2 md:gap-3 mb-8 md:mb-10">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.slug
                      ? `${colors.bg} ${colors.text}`
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} w-4 h-4 flex items-center justify-center`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {currentCategory && (
              <div className="mb-8">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                  {currentCategory.name}赛事资讯
                </h1>
                <p className="text-foreground-600 text-sm md:text-base">{currentCategory.description}</p>
              </div>
            )}

            {categoryArticles.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">暂无相关文章</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group bg-background-100 rounded-xl overflow-hidden transition-all duration-300 hover:scale-[1.02] cursor-pointer"
                  >
                    <div className="relative w-full h-48 md:h-52 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute top-3 left-3">
                        <span className={`px-3 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${colors.bg} ${colors.text}`}>
                          {article.categoryName}
                        </span>
                      </div>
                    </div>
                    <div className="p-4 md:p-5">
                      <h2 className="font-heading font-semibold text-base md:text-lg text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                        {article.title}
                      </h2>
                      <p className="text-foreground-600 text-xs md:text-sm leading-relaxed line-clamp-2 mb-3">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-500">
                        <span>{article.publishedAt}</span>
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line w-3 h-3 flex items-center justify-center"></i>
                          {article.readCount.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${currentCategory?.name || ''}赛事资讯 - 开云体育`,
            description: currentCategory?.description || '',
            url: `https://www.kaiyunsports.com/category/${activeCategory}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '开云体育',
              url: 'https://www.kaiyunsports.com',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.kaiyunsports.com/' },
                { '@type': 'ListItem', position: 2, name: '赛事分类', item: 'https://www.kaiyunsports.com/category/football' },
                { '@type': 'ListItem', position: 3, name: currentCategory?.name || '' },
              ],
            },
          }),
        }}
      />
    </>
  );
}